/**
 * useEnergyCertificateForm — the energy-cert view-model controller.
 *
 * Decodes the persisted DTO to an editable view model and exposes FIELD-GRANULAR
 * setters (WAVE2-DECISIONS C1). Each setter writes ONLY the wire field(s) it owns
 * (+ declared cross-derived siblings), computed from the LIVE persisted DTO
 * (`getState()`, never a stale mount snapshot). NO whole-DTO re-encode ships —
 * the pure `encodeEnergy` is a test oracle only; the sub-encoders
 * (`encodeWalls`/`encodeHeaters`/`encodeVentilation`/`encodePhotovoltaics`) compute
 * the cross-derived slices.
 *
 * Cross-derived clusters:
 *   - Heaters → whole `heaters` array (the `.some()` handover flag rewrites siblings).
 *   - Insulation SWAP → view roof ↔ dto basement (C4; setters map to the crossed field).
 *   - Ventilation Set deselect → clears the 4 sibling fields (C5) — handled in the panel's
 *     local Set state + `patchVentilation`.
 *   - buildingType leaving MIXED → blanks commercial fields.
 *   - Shape change → prune/extend walls (prune-as-handler, NOT a mount effect).
 *
 * Heater rows carry a STABLE per-row local id (C6) kept in `idsRef`, never persisted
 * (dropped by encode).
 */

import { useCallback, useMemo, useRef } from 'react'

import {
  BuildingType,
  type BasementCeilingType,
  type BasementType,
  type BuildingLocationType,
  type ConstructionType,
  type GlobalShapeType,
  type LightweightConstructionType,
  type OrientationType,
  type PhotovoltaicSystemType,
  type RoofConditionType,
  type RoofPitchType,
  type SolidConstructionType,
  type ThermalBridgeType,
  type WindowType
} from 'constants/purchaseFlow'
import { EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'
import type { Nullable } from 'models/helpers'
import type { CartItemId } from 'models/purchaseFlowRevamp'
import type { HeatingSystemItemDTO } from 'models/purchaseFlow/energyCertificateOfNeedDTO'
import { toNumber } from 'utils/formatting/numberUtils'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import {
  decodeEnergy,
  encodeHeaters,
  encodePhotovoltaics,
  encodeVentilation,
  encodeWalls,
  type EnergyPhotovoltaicView,
  type EnergyVentilationView
} from './energyCertificateOfNeed.codec'
import { ENERGY_CERTIFICATE_DEFAULT, NEW_HEATER, type EnergyCertificateConfigDTO } from './energyCertificateOfNeed.dto'
import { calculateWallLengths, recomputeWalls, type EnergyWalls } from './energyCertificateOfNeed.walls'
import { useEnergyCertificateConfig } from './useEnergyCertificateConfig'

let heaterIdCounter = 0
const genHeaterId = (): string => `heater-${++heaterIdCounter}`

const HEATED = new Set<string>(['HEATED_WINDOW_SOLAR_GAIN', 'HEATED_NO_WINDOW'])

export function useEnergyCertificateForm(cartItemId: CartItemId) {
  const { value, patch } = useEnergyCertificateConfig(cartItemId)
  const key = makeConfigKey(cartItemId, 'energy_certificate_of_need')

  // Decode the persisted DTO → editable view (re-derived whenever the DTO changes).
  const view = useMemo(() => decodeEnergy(value), [value])

  // Read the LIVE field-merged DTO for cross-derived recompute (C1 / D-W2-11).
  const readLive = useCallback((): EnergyCertificateConfigDTO => {
    const raw = usePfStore.getState().productConfigs[key] as Partial<EnergyCertificateConfigDTO> | undefined
    return { ...ENERGY_CERTIFICATE_DEFAULT, ...raw }
  }, [key])

  // ── Stable heater ids (C6) ─────────────────────────────────────────────────
  // Kept in lockstep with the heaters array; the render-time reconcile is a
  // safety net for mount/hydrate (add/remove manage ids explicitly).
  const idsRef = useRef<string[]>([])
  if (idsRef.current.length !== view.heaters.length) {
    idsRef.current = view.heaters.map((_, i) => idsRef.current[i] ?? genHeaterId())
  }
  const heaterIds = idsRef.current

  // ── Scalar property setters ─────────────────────────────────────────────────
  const setBuildingType = useCallback(
    (buildingType: BuildingType) =>
      patch(
        buildingType === BuildingType.MIXED_USE_BUILDING
          ? { buildingType }
          : { buildingType, commercialSpaceInSquareMeters: 0, typeOfUseCommercial: '' } // leaving MIXED blanks commercial
      ),
    [patch]
  )
  const setBuildingLocation = useCallback((buildingLocation: BuildingLocationType) => patch({ buildingLocation }), [patch])
  const setResidentialUnit = useCallback((livingUnits: number) => patch({ livingUnits }), [patch])
  const setCommercialArea = useCallback((v: string) => patch({ commercialSpaceInSquareMeters: v === '' ? 0 : parseInt(v) }), [patch])
  const setCommercialAreaType = useCallback((typeOfUseCommercial: string) => patch({ typeOfUseCommercial }), [patch])
  const setConstructionYear = useCallback((buildingYear: string) => patch({ buildingYear }), [patch])
  const setLivingSpace = useCallback((v: string) => patch({ totalLivingArea: toNumber(v) }), [patch])

  // ── Building layout ─────────────────────────────────────────────────────────
  const setOrientation = useCallback((orientation: OrientationType) => patch({ orientation }), [patch])

  /** Prune-as-handler (spec §5): shape change recomputes the wall set PURELY here
   *  (never a mount effect → F-B1 unreachable). The walls left behind are kept per shape, so
   *  switching back restores them instead of starting from empty walls. */
  const setShape = useCallback(
    (buildingShape: GlobalShapeType) => {
      const current = readLive()
      const live = decodeEnergy(current)
      const wallDimensionsByShape = { ...current.wallDimensionsByShape, [live.globalShapeType]: current.wallDimensions ?? [] }
      const remembered = wallDimensionsByShape[buildingShape]
      const wallDimensions = remembered?.length ? remembered : encodeWalls(recomputeWalls(buildingShape, live.walls))
      patch({ buildingShape, wallDimensions, wallDimensionsByShape })
    },
    [readLive, patch]
  )

  /** Wall edit → run the shape auto-compute (c=a, …) then persist the whole set. */
  const setWalls = useCallback(
    (nextWalls: EnergyWalls) => {
      const live = decodeEnergy(readLive())
      patch({ wallDimensions: encodeWalls(calculateWallLengths(live.globalShapeType, nextWalls)) })
    },
    [readLive, patch]
  )

  // ── Construction method ──────────────────────────────────────────────────────
  const setConstructionType = useCallback((constructionType: ConstructionType) => patch({ constructionType }), [patch])
  const setLightweightConstructionType = useCallback((wallType: LightweightConstructionType) => patch({ wallType }), [patch])
  const setSolidConstructionType = useCallback((wallType: SolidConstructionType) => patch({ wallType }), [patch])
  const setRoofCondition = useCallback((roofCondition: RoofConditionType) => patch({ roofCondition }), [patch])
  const setRoofPitch = useCallback((roofPitch: RoofPitchType) => patch({ roofPitch }), [patch])
  const setSubsequentRoofInsulation = useCallback((v: Nullable<number>) => patch({ subsequentRoofInsulation: v ?? 0 }), [patch])
  const setSubsequentBasementInsulation = useCallback((v: Nullable<number>) => patch({ subsequentBasementInsulation: v ?? 0 }), [patch])
  const setBasementType = useCallback(
    (basement: BasementType) =>
      // soilDepth/heatedBasementArea only persist for heated basements (encode forces null otherwise).
      patch(HEATED.has(basement) ? { basement } : { basement, soilDepth: null, heatedBasementArea: null }),
    [patch]
  )
  const setBasementCeilingType = useCallback((basementCeilingType: BasementCeilingType) => patch({ basementCeilingType }), [patch])
  const setSoilDepth = useCallback((v: string) => patch({ soilDepth: toNumber(v) }), [patch])
  const setHeatedBasementArea = useCallback((v: Nullable<number>) => patch({ heatedBasementArea: v }), [patch])
  const setWindowType = useCallback((windowType: WindowType) => patch({ windowType }), [patch])
  const setYearOfManufacture = useCallback((v: Nullable<number>) => patch({ windowConstructionYear: v }), [patch])
  const setNumberOfFullFloors = useCallback((v: Nullable<number>) => patch({ numberFullFloors: v ?? 1 }), [patch])
  const setFloorHeight = useCallback((v: string) => patch({ floorHeightMeters: toNumber(v) }), [patch])
  const setThermalBridge = useCallback((thermalBridges: ThermalBridgeType) => patch({ thermalBridges }), [patch])

  // ── Heaters (whole-array; C1) ─────────────────────────────────────────────────
  const setHeater = useCallback(
    (id: string, partial: Partial<HeatingSystemItemDTO>) => {
      const index = heaterIds.indexOf(id)
      if (index < 0) return
      const heaters = decodeEnergy(readLive()).heaters
      patch({ heaters: encodeHeaters(heaters.map((h, i) => (i === index ? { ...h, ...partial } : h))) })
    },
    [heaterIds, readLive, patch]
  )

  /** Total-heating-output cross-heater recompute (legacy `updateFirstHeatingSystem`):
   *  editing a non-first heater's share sets the first heater's share = 100 − Σ(others). */
  const setHeaterShare = useCallback(
    (id: string, shareValue: Nullable<number>) => {
      const index = heaterIds.indexOf(id)
      if (index < 0) return
      const heaters = decodeEnergy(readLive()).heaters
      if (heaters.length <= 1 || index === 0) {
        patch({ heaters: encodeHeaters(heaters.map((h, i) => (i === index ? { ...h, totalHeatingOutput: shareValue } : h))) })
        return
      }
      const totalEditable = heaters
        .slice(1)
        .reduce((acc, h, i) => acc + (i + 1 === index ? shareValue || 0 : h.totalHeatingOutput || 0), 0)
      const next = heaters.map((h, i) => {
        if (i === index) return { ...h, totalHeatingOutput: shareValue }
        if (i === 0) return { ...h, totalHeatingOutput: 100 - totalEditable }
        return h
      })
      patch({ heaters: encodeHeaters(next) })
    },
    [heaterIds, readLive, patch]
  )

  const addHeater = useCallback(() => {
    const heaters = decodeEnergy(readLive()).heaters
    idsRef.current = [...heaterIds, genHeaterId()]
    patch({ heaters: encodeHeaters([...heaters, { ...NEW_HEATER, heatingCombinedWith: new Set() }]) })
  }, [heaterIds, readLive, patch])

  const removeHeater = useCallback(
    (id: string) => {
      const index = heaterIds.indexOf(id)
      if (index <= 0) return // never remove the first heater
      const heaters = decodeEnergy(readLive()).heaters
      idsRef.current = heaterIds.filter((_, i) => i !== index)
      patch({ heaters: encodeHeaters(heaters.filter((_, i) => i !== index)) })
    },
    [heaterIds, readLive, patch]
  )

  // ── Photovoltaics + ventilation ─────────────────────────────────────────────
  // The panels own the ephemeral local Set/mid-edit state; these setters persist
  // the encoded wire slice. C5: the ventilation panel clears the 4 sibling fields
  // on VENTILATION_SYSTEM_AVAILABLE deselect BEFORE calling `patchVentilation`.
  const patchPhotovoltaics = useCallback(
    (system: PhotovoltaicSystemType, features: EnergyPhotovoltaicView) => patch({ photovoltaics: encodePhotovoltaics(system, features) }),
    [patch]
  )
  const patchVentilation = useCallback((v: EnergyVentilationView) => patch(encodeVentilation(v)), [patch])

  // ── Additional information ────────────────────────────────────────────────────
  const setReason = useCallback((reasonForEnergyPass: EnergyPassReason) => patch({ reasonForEnergyPass }), [patch])
  const setMessage = useCallback((messageToExpert: string) => patch({ messageToExpert }), [patch])

  return {
    view,
    heaterIds,
    setBuildingType,
    setBuildingLocation,
    setResidentialUnit,
    setCommercialArea,
    setCommercialAreaType,
    setConstructionYear,
    setLivingSpace,
    setOrientation,
    setShape,
    setWalls,
    setConstructionType,
    setLightweightConstructionType,
    setSolidConstructionType,
    setRoofCondition,
    setRoofPitch,
    setSubsequentRoofInsulation,
    setSubsequentBasementInsulation,
    setBasementType,
    setBasementCeilingType,
    setSoilDepth,
    setHeatedBasementArea,
    setWindowType,
    setYearOfManufacture,
    setNumberOfFullFloors,
    setFloorHeight,
    setThermalBridge,
    setHeater,
    setHeaterShare,
    addHeater,
    removeHeater,
    patchPhotovoltaics,
    patchVentilation,
    setReason,
    setMessage,
  }
}

export type UseEnergyCertificateForm = ReturnType<typeof useEnergyCertificateForm>
